import { JourneyUiDefinition } from '../models/onboarding.types';
import { ACCOUNT_REVALIDATION_TRIGGERS } from './account-triggers';

const BASE = 'onboarding.journeys.carrier_first_bid';
const STEPS = `${BASE}.steps`;
const PROFILE = `${STEPS}.profile`;

/**
 * Jornada "Transportadora · Primeiro lance" (chave do backend: carrier-first-bid). Só textos,
 * rotas, tutoriais e gatilhos: a regra de cada etapa mora nos validadores do backend.
 */
export const CARRIER_FIRST_BID_JOURNEY: JourneyUiDefinition = {
  journeyKey: 'carrier-first-bid',
  titleKey: `${BASE}.title`,
  subtitleKey: `${BASE}.subtitle`,
  welcome: {
    titleKey: `${BASE}.welcome.title`,
    leadKey: `${BASE}.welcome.lead`,
    leadReadyKey: `${BASE}.welcome.lead_ready`,
    benefitKeys: [`${BASE}.welcome.benefit_real`, `${BASE}.welcome.benefit_profile`, `${BASE}.welcome.benefit_pause`],
  },
  completion: {
    titleKey: `${BASE}.completion.title`,
    messageKey: `${BASE}.completion.message`,
    actions: [
      { id: 'onboarding-completion-carrier-offers-link', labelKey: `${BASE}.completion.track_offers`, route: '/app/carriers/offers' },
      {
        id: 'onboarding-completion-carrier-profile-link',
        labelKey: `${BASE}.completion.complete_profile`,
        route: '/app/configurations-carrier/companies',
      },
    ],
  },
  steps: {
    company: {
      titleKey: `${STEPS}.company.title`,
      descriptionKey: `${STEPS}.company.description`,
      whyKey: `${STEPS}.company.why`,
      icon: 'domain',
      route: '/app/configurations-carrier/companies',
      actionLabelKey: `${STEPS}.company.action`,
      tutorialKey: 'carrier-create-company',
      statusMessageKeys: { AVAILABLE: `${STEPS}.company.available`, IN_PROGRESS: `${STEPS}.company.available` },
      readyMessageKey: `${STEPS}.company.ready`,
      evidenceLines: [{ evidence: 'empresas', labelKey: `${STEPS}.company.evidence_companies`, hideWhenZero: true }],
    },
    profile: {
      titleKey: `${PROFILE}.title`,
      descriptionKey: `${PROFILE}.description`,
      whyKey: `${PROFILE}.why`,
      icon: 'fact_check',
      route: '/app/configurations-carrier/companies',
      actionLabelKey: `${PROFILE}.action`,
      tutorialKey: 'complete-carrier-profile',
      recommended: true,
      adminOnly: true,
      statusMessageKeys: {
        AVAILABLE: `${PROFILE}.available`,
        IN_PROGRESS: `${PROFILE}.available`,
        BLOCKED: `${PROFILE}.blocked`,
        COMPLETED: `${PROFILE}.completed`,
      },
      readyMessageKey: `${PROFILE}.ready`,
      checklist: [
        { evidence: 'cargas', labelKey: `${PROFILE}.check_cargo` },
        { evidence: 'veiculos', labelKey: `${PROFILE}.check_vehicles` },
        { evidence: 'ufs', labelKey: `${PROFILE}.check_states` },
        { evidence: 'modalidades', labelKey: `${PROFILE}.check_freight_modes` },
        { evidence: 'certificacoes', labelKey: `${PROFILE}.check_certifications`, bonus: true },
        { evidence: 'logo', labelKey: `${PROFILE}.check_logo`, bonus: true },
      ],
    },
    team: {
      titleKey: `${STEPS}.team.title`,
      descriptionKey: `${STEPS}.team.description`,
      whyKey: `${STEPS}.team.why`,
      icon: 'group_add',
      route: '/app/configurations-carrier/users',
      actionLabelKey: `${STEPS}.team.action`,
      tutorialKey: 'carrier-link-users',
      statusMessageKeys: { BLOCKED: `${STEPS}.team.blocked` },
      readyMessageKey: `${STEPS}.team.ready`,
      evidenceLines: [{ evidence: 'usuarios', labelKey: `${STEPS}.team.evidence_users`, hideWhenZero: true }],
    },
    partnership: {
      titleKey: `${STEPS}.partnership.title`,
      descriptionKey: `${STEPS}.partnership.description`,
      whyKey: `${STEPS}.partnership.why`,
      icon: 'handshake',
      route: '/app/carriers/my-shippers?tab=my_invites',
      actionLabelKey: `${STEPS}.partnership.action`,
      tutorialKey: 'accept-shipper-invite',
      statusMessageKeys: {
        AVAILABLE: `${STEPS}.partnership.available`,
        WAITING_EXTERNAL: `${STEPS}.partnership.waiting`,
        BLOCKED: `${STEPS}.partnership.blocked`,
        COMPLETED: `${STEPS}.partnership.completed`,
      },
      readyMessageKey: `${STEPS}.partnership.ready`,
      evidenceLines: [
        { evidence: 'embarcadores', labelKey: `${STEPS}.partnership.evidence_shippers`, hideWhenZero: true },
        { evidence: 'convitesPendentes', labelKey: `${STEPS}.partnership.evidence_pending_invites`, hideWhenZero: true },
      ],
    },
    first_bid: {
      titleKey: `${STEPS}.first_bid.title`,
      descriptionKey: `${STEPS}.first_bid.description`,
      whyKey: `${STEPS}.first_bid.why`,
      icon: 'gavel',
      route: '/app/carriers/offers',
      actionLabelKey: `${STEPS}.first_bid.action`,
      tutorialKey: 'bid-spot-offer',
      statusMessageKeys: {
        AVAILABLE: `${STEPS}.first_bid.available`,
        WAITING_EXTERNAL: `${STEPS}.first_bid.waiting`,
        BLOCKED: `${STEPS}.first_bid.blocked`,
      },
      evidenceLines: [
        { evidence: 'ofertasAguardandoLance', labelKey: `${STEPS}.first_bid.evidence_waiting_offers`, hideWhenZero: true },
      ],
    },
  },
  revalidateOn: [
    ...ACCOUNT_REVALIDATION_TRIGGERS,
    { method: 'POST', urlPattern: String.raw`/leilao/(carga-empresa|veiculo-empresa|uf-atendida|modalidade-empresa|certificacao-empresa)(/|\?|$)` },
    { method: 'DELETE', urlPattern: '/leilao/(carga-empresa|veiculo-empresa|uf-atendida|modalidade-empresa|certificacao-empresa)/' },
    { method: 'POST', urlPattern: '/leilao/convite/' },
    { method: 'DELETE', urlPattern: '/leilao/parceria/' },
    { method: 'POST', urlPattern: '/leilao/lance/oferta' },
    { method: 'DELETE', urlPattern: String.raw`/leilao/lance(\?|$)` },
  ],
  reminder: {
    stepKey: 'profile',
    labelKey: `${PROFILE}.reminder`,
    doneEvidence: 'itensCompletos',
    totalEvidence: 'itensTotal',
  },
};
